import { Component, type ErrorInfo, type ReactNode } from "react";
import { Banner, BlockStack, Button, Card, Page, Text } from "@shopify/polaris";

/**
 * Catches a render-time throw and shows the merchant something.
 *
 * Without this, a single component error unmounts the entire React tree and the
 * merchant is left looking at blank space inside the Shopify admin — no message,
 * no retry, no indication the app is even there. That is what #2752 looked like:
 * a cold load of /conversations rendered nothing at all, including the app's own
 * navigation, and there was no way to tell from the outside whether the app had
 * crashed, failed to load, or simply had nothing to show.
 *
 * Two boundaries are used (see App.tsx):
 *
 *   - a ROUTE-level one wrapping only the routed page, so a page that throws
 *     leaves the navigation intact and the merchant can click away to a working
 *     page instead of being stranded. The navigation is now App Bridge's
 *     `<ui-nav-menu>`, which sits OUTSIDE this boundary and is rendered by the
 *     Shopify admin rather than by us — so a crashing page cannot take it down
 *     even in principle. It used to be a Polaris `<Frame>` inside the iframe,
 *     which is what made this split necessary in the first place;
 *   - an APP-level one outside that, to catch a failure in the providers or in
 *     the shell itself, which the route boundary cannot see.
 *
 * `resetKey` is the route path. Changing it clears the error, so navigating away
 * from a broken page actually recovers rather than pinning the fallback forever.
 *
 * The error is re-thrown to console.error deliberately. Swallowing it would
 * trade one invisible failure for another; this way the fallback is visible to
 * the merchant AND the stack is visible to whoever is debugging.
 */
interface Props {
  children: ReactNode;
  /** Changing this clears the error — pass the route path. */
  resetKey?: string;
  /** Shown above the message. Distinguishes app-level from route-level. */
  title?: string;
  /** Rendered inside a Polaris Page (route-level) or bare (app-level). */
  withPage?: boolean;
}

interface State {
  error: Error | null;
  resetKey?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    // Navigating to a different route clears a previous route's error.
    if (state.error && props.resetKey !== state.resetKey) {
      return { error: null, resetKey: props.resetKey };
    }
    if (state.resetKey !== props.resetKey) return { resetKey: props.resetKey };
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep the stack reachable. The fallback tells the merchant something is
    // wrong; this is what tells a developer WHAT.
    console.error("[brilio] unhandled error in the admin app", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const body = (
      <Card>
        <BlockStack gap="300">
          <Banner tone="critical" title={this.props.title ?? "Something went wrong"}>
            <p>
              This part of the app failed to load. Your store and your
              assistant are unaffected — nothing has been changed.
            </p>
          </Banner>
          <Text as="p" tone="subdued" variant="bodySm">
            {error.message}
          </Text>
          <div>
            <Button onClick={() => window.location.reload()}>Reload</Button>
          </div>
        </BlockStack>
      </Card>
    );

    return this.props.withPage ? <Page title="Brilio Assistant">{body}</Page> : body;
  }
}

export default ErrorBoundary;
